const DEFAULT_CONCURRENCY_LIMIT = 3;
const MAX_CHUNK_SIZE = 100;

export type PaginationOptions<T, R> = {
  items: T[];
  options?: {
    chunkSize?: number;
    concurrencyLimit?: number;
  };
  mapper: (item: T[], index: number) => Promise<R>;
};

/**
 * Paginates items in chunks and maps them with a concurrency limit.
 *
 * Note: concurrencyLimit should be tuned per API/endpoint by the caller.
 * if in doubt, pick the default concurrency limit of 3, which is a safe choice.
 */
export const paginateWithConcurrency = async <T, R>({
  items,
  options = {},
  mapper,
}: PaginationOptions<T, R>): Promise<R[]> => {
  const {
    chunkSize = MAX_CHUNK_SIZE,
    concurrencyLimit = DEFAULT_CONCURRENCY_LIMIT,
  } = options;

  if (items.length === 0) {
    return [];
  }

  const createChunks = <T>(items: T[], chunkSize: number): T[][] => {
    if (chunkSize <= 0) {
      throw new Error('Chunk size must be greater than 0');
    }

    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += chunkSize) {
      chunks.push(items.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const mapWithConcurrency = async <U, V>(
    items: U[],
    limit: number,
    mapper: (item: U, index: number) => Promise<V>
  ): Promise<V[]> => {
    const results: V[] = new Array(items.length);
    let nextIndex = 0;

    const workers = Array.from({ length: Math.max(1, limit) }, async () => {
      let currentIndex: number;
      while ((currentIndex = nextIndex++) < items.length) {
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    });

    await Promise.all(workers);
    return results;
  };

  const chunks = createChunks(items, chunkSize);
  return mapWithConcurrency(chunks, concurrencyLimit, mapper);
};
